import React, { useState, useEffect } from 'react';
import { Modal, Typography, Spin, App, Space, Card, Button, Input, List, Row, Col, InputNumber } from 'antd';
import { PlusOutlined, MinusOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { deviceSaleReturn } from '../services/posApi';
import { terminalIntegrationService } from '../services/terminalIntegration';

import NumbersKeypad from "./Sale/NumbersKeypad";


const { Title, Text } = Typography;

const SaleReturnModal = ({ visible, onClose, saleId, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1); // 1 - выбор товаров, 2 - выбор оплат
    const [prepareData, setPrepareData] = useState(null);
    const [selectedItems, setSelectedItems] = useState({});
    const [selectedPayments, setSelectedPayments] = useState({});
    const [activePaymentInput, setActivePaymentInput] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [processingModal, setProcessingModal] = useState({
        visible: false,
        title: '',
        description: ''
    });
    const [errorModalVisible, setErrorModalVisible] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const { message: messageApi } = App.useApp();


    // Загрузка данных prepare
    const loadPrepareData = async () => {
        setLoading(true);
        try {
            const response = await deviceSaleReturn.getPrepare(saleId);
            if (response.status === 'success') {
                setPrepareData(response.data);
                // Инициализируем выбранные товары нулями
                const initialItems = {};
                response.data.available_items.forEach(item => {
                    initialItems[item.id] = 0;
                });
                setSelectedItems(initialItems);

                // Инициализируем выбранные оплаты нулями
                const initialPayments = {};
                response.data.available_payments.forEach(payment => {
                    initialPayments[payment.payment_method_id] = '';
                });
                setSelectedPayments(initialPayments);
            } else {
                messageApi.error(response.message || 'Ошибка при получении данных для возврата');
            }
        } catch (error) {
            console.error('Ошибка загрузки данных prepare:', error);
            messageApi.error('Не удалось загрузить данные для возврата');
        } finally {
            setLoading(false);
        }
    };

    // Загрузка данных при открытии модала
    useEffect(() => {
        if (visible && saleId) {
            loadPrepareData();
        }
    }, [visible, saleId]);

    // useEffect для автоматического заполнения способов оплаты
    useEffect(() => {
        if (prepareData && step === 2) {
            const itemsTotal = calculateSelectedItemsTotal();
            const paymentsTotal = calculateSelectedPaymentsTotal();
            const remainder = itemsTotal - paymentsTotal;

            // Автоматически заполняем только если есть остаток и все способы оплаты пусты
            if (remainder > 0) {
                const allPaymentsEmpty = prepareData.available_payments.every(payment => {
                    const paymentId = payment.payment_method_id.toString();
                    const currentValue = selectedPayments[paymentId] || '';
                    return currentValue === '' || parseFloat(currentValue) === 0;
                });

                if (allPaymentsEmpty && prepareData.available_payments.length > 0) {
                    const firstPayment = prepareData.available_payments[0];
                    const firstPaymentId = firstPayment.payment_method_id.toString();
                    const amountToFill = Math.min(remainder, firstPayment.available_amount);
                    setSelectedPayments(prev => ({
                        ...prev,
                        [firstPaymentId]: amountToFill.toString()
                    }));
                }
            }
        }
    }, [step, JSON.stringify(selectedItems), JSON.stringify(selectedPayments), prepareData]);

    // Сброс данных при закрытии модала
    const handleModalClose = () => {
        setPrepareData(null);
        setStep(1);
        setSelectedItems({});
        setSelectedPayments({});
        setActivePaymentInput(null);
        onClose();
    };

    // Изменение количества товара
    const handleItemQuantityChange = (itemId, change) => {
        setSelectedItems(prev => {
            const item = prepareData.available_items.find(i => i.id === itemId);
            const currentQuantity = prev[itemId] || 0;
            const newQuantity = Math.max(0, Math.min(item.available_quantity, currentQuantity + change));
            return {
                ...prev,
                [itemId]: newQuantity
            };
        });
    };

    // Переход к следующему шагу
    const handleNextStep = () => {
        const hasSelectedItems = Object.values(selectedItems).some(qty => qty > 0);
        if (!hasSelectedItems) {
            messageApi.error('Выберите хотя бы один товар для возврата');
            return;
        }
        setStep(2);
    };

    // Обработка нажатия клавиш для оплат
    const handlePaymentKeyPress = (key) => {
        if (!activePaymentInput) return;

        setSelectedPayments(prev => {
            const currentValue = prev[activePaymentInput] || '';
            let newValue = currentValue;

            if (key === 'backspace') {
                newValue = currentValue.slice(0, -1);
            } else if (key === '00') {
                newValue = currentValue + '00';
            } else {
                newValue = currentValue + key;
            }

            // Проверка лимита
            const payment = prepareData.available_payments.find(p => p.payment_method_id.toString() === activePaymentInput.toString());
            if (payment && parseFloat(newValue) > payment.available_amount) {
                return prev; // Не изменяем, если превышен лимит
            }

            return {
                ...prev,
                [activePaymentInput]: newValue
            };
        });
    };

    // Форматирование суммы
    const formatAmount = (amount) => {
        if (!amount) return '0.00';
        return new Intl.NumberFormat('ru-RU', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    // Создание возврата
    const handleCreateReturn = async () => {
        // Подготовка данных для возврата
        const returnItems = Object.entries(selectedItems)
            .filter(([_, quantity]) => quantity > 0)
            .map(([itemId, quantity]) => ({
                id: itemId,
                quantity: quantity
            }));

        const returnPayments = Object.entries(selectedPayments)
            .filter(([_, amount]) => amount && parseFloat(amount) > 0)
            .map(([paymentMethodId, amount]) => {
                const payment = prepareData.available_payments.find(p => p.payment_method_id.toString() === paymentMethodId);
                return {
                    type: payment.payment_type,
                    payment_method_id: parseInt(paymentMethodId),
                    sum: parseFloat(amount),
                    payment_details: payment.payment_details,
                    pos_type: payment.pos_type // Добавляем pos_type в returnPayments
                };
            });

        if (returnItems.length === 0) {
            messageApi.error('Выберите товары для возврата');
            return;
        }

        if (returnPayments.length === 0) {
            messageApi.error('Укажите сумму для возврата');
            return;
        }

        setActionLoading(true);

        try {
            // Проверяем, есть ли карточные платежи для возврата через терминал
            const cardPayments = returnPayments.filter(payment => payment.type === 'card');

            if (cardPayments.length > 0) {
                // Обрабатываем карточные возвраты через терминал
                for (const cardPayment of cardPayments) {
                    const paymentDetails = cardPayment.payment_details;

                    // Проверяем наличие pos_type и базовых данных для возврата
                    if (!cardPayment.pos_type) {
                        messageApi.error('Тип терминала не определен для возврата');
                        return;
                    }

                    // Проверка наличия данных для возврата в зависимости от типа терминала
                    if (cardPayment.pos_type === 'freedom') {
                        const channel = parseInt(paymentDetails.channel);
                        if (channel === 2) {
                            // QR - обязателен tagRRN (вообще по документации transactionId но они его даже не передают)
                            if (!paymentDetails.tagRRN) {
                                messageApi.error('Отсутствует ID транзакции для возврата по QR');
                                return;
                            }
                        } else {
                            // Карты (channel 0, 1) - обязателен RRN
                            if (!paymentDetails.tagRRN) {
                                messageApi.error('Отсутствует RRN транзакции для возврата по карте');
                                return;
                            }
                        }
                    } else {
                        // Для Halyk и Kaspi
                        const requiredFields = cardPayment.pos_type === 'halyk'
                            ? ['tagRRN']
                            : ['chequeInfo'];

                        const missingFields = requiredFields.filter(field => !paymentDetails || !paymentDetails[field]);
                        if (missingFields.length > 0) {
                            messageApi.error(`Отсутствуют данные для возврата: ${missingFields.join(', ')}`);
                            return;
                        }
                    }

                    // Показываем модал обработки
                    setProcessingModal({
                        visible: true,
                        title: 'Обработка возврата',
                        description: 'Инициализация...'
                    });

                    try {
                        const refundResult = await terminalIntegrationService.processRefund(
                            {
                                posType: cardPayment.pos_type,
                                amount: cardPayment.sum,
                                transactionData: paymentDetails
                            },
                            (statusMessage) => {
                                setProcessingModal(prev => ({
                                    ...prev,
                                    description: statusMessage
                                }));
                            }
                        );

                        // Скрываем модал обработки
                        setProcessingModal({ visible: false, title: '', description: '' });

                        if (!refundResult.success) {
                            showErrorModal(refundResult.error);
                            return;
                        }

                        // Обновляем payment_details с данными возврата
                        cardPayment.payment_details = refundResult.refundDetails;

                    } catch (error) {
                        setProcessingModal({ visible: false, title: '', description: '' });
                        showErrorModal(error.message);
                        return;
                    }
                }
            }

            // Создаем возврат в системе
            const returnData = {
                return_id: saleId,
                items: returnItems,
                payments: returnPayments
            };

            const response = await deviceSaleReturn.createReturn(returnData);
            if (response.status === 'success') {
                messageApi.success('Возврат успешно создан');
                if (onSuccess) onSuccess();
                handleModalClose();
            } else {
                messageApi.error(response.message || 'Ошибка при создании возврата');
            }
        } catch (error) {
            console.error('Ошибка при создании возврата:', error);
            messageApi.error('Не удалось создать возврат');
        } finally {
            setActionLoading(false);
            setProcessingModal({ visible: false, title: '', description: '' });
        }
    };

    const showErrorModal = (errorMessage) => {
        setErrorMessage(errorMessage);
        setErrorModalVisible(true);
    };

    // Функция расчета общей суммы выбранных товаров
    const calculateSelectedItemsTotal = () => {
        if (!prepareData) return 0;

        return Object.entries(selectedItems).reduce((total, [itemId, quantity]) => {
            const item = prepareData.available_items.find(i => i.id == itemId);
            return total + (item ? parseFloat(item.unit_price) * quantity : 0);
        }, 0);
    };

    // Функция расчета общей суммы введенных оплат
    const calculateSelectedPaymentsTotal = () => {
        return Object.values(selectedPayments).reduce((total, amount) => {
            return total + (parseFloat(amount) || 0);
        }, 0);
    };

    const isValidPayments = () => {
        const itemsTotal = calculateSelectedItemsTotal();
        const paymentsTotal = calculateSelectedPaymentsTotal();
        return Math.abs(itemsTotal - paymentsTotal) < 0.01;
    };

    // Функция автоматического заполнения остатка
    const handlePaymentInputChange = (paymentMethodId, value) => {
        const numValue = parseFloat(value) || 0;
        const payment = prepareData.available_payments.find(p => p.payment_method_id.toString() === paymentMethodId);

        // Проверка лимита
        if (numValue > payment.available_amount) {
            value = payment.available_amount.toString();
        }

        setSelectedPayments(prev => {
            const newPayments = {
                ...prev,
                [paymentMethodId]: value
            };

            // Не делаем автоматическое распределение - позволяем пользователю самому управлять
            return newPayments;
        });
    };

    // Рендер шага выбора товаров
    const renderItemsStep = () => (
        <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Text>Выберите товары для возврата:</Text>
                <List
                    dataSource={prepareData.available_items}
                    renderItem={item => (
                        <List.Item style={{ padding: '16px 0' }}>
                            <Row justify="space-between" align="middle" style={{ width: '100%' }}>
                                <Col flex="auto">
                                    <Space direction="vertical" size="small">
                                        <Text>{item.name}</Text>
                                    <Space>
                                        <Button
                                            icon={<MinusOutlined />}
                                            onClick={() => handleItemQuantityChange(item.id, -1)}
                                            disabled={selectedItems[item.id] <= 0}
                                            style={{
                                                width: '72px',
                                                height: '72px',
                                                fontSize: '24px',
                                                lineHeight: '30px',
                                                letterSpacing: '0.36px',
                                                fontWeight: 'bold',
                                                padding: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        />
                                        <InputNumber
                                            value={selectedItems[item.id] || 0}
                                            min={0}
                                            max={item.available_quantity}
                                            onChange={(value) => setSelectedItems(prev => ({
                                                ...prev,
                                                [item.id]: value || 0
                                            }))}
                                            style={{
                                                width: '72px',
                                                height: '72px',
                                                fontSize: '24px',
                                                lineHeight: '30px',
                                                letterSpacing: '0.36px',
                                                fontWeight: 'bold',
                                                padding: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                            controls={false}
                                            inputMode="numeric"
                                        />
                                        <Button
                                            icon={<PlusOutlined />}
                                            onClick={() => handleItemQuantityChange(item.id, 1)}
                                            disabled={selectedItems[item.id] >= item.available_quantity}
                                            style={{
                                                width: '72px',
                                                height: '72px',
                                                fontSize: '24px',
                                                lineHeight: '30px',
                                                letterSpacing: '0.36px',
                                                fontWeight: 'bold',
                                                padding: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        />
                                    </Space>
                                    </Space>
                                </Col>
                            </Row>
                        </List.Item>
                    )}
                />
            </Space>
        </div>
    );

    // Рендер шага выбора оплат
    const renderPaymentsStep = () => {
        const itemsTotal = calculateSelectedItemsTotal();
        const paymentsTotal = calculateSelectedPaymentsTotal();
        const remainder = itemsTotal - paymentsTotal;

        const paymentsCount = prepareData.available_payments.length;
        const getColSpan = () => {
            if (paymentsCount === 1) return 24;
            if (paymentsCount === 2) return 12;
            return 12;
        };


        return (
            <div style={{ height: '518px', overflowY: 'clip' }}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div>
                        <div>
                            <Row gutter={[8, 4]}>
                                <Col span={8}>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        Сумма: {formatAmount(itemsTotal)} ₸
                                    </Text>
                                </Col>
                                <Col span={8}>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        Введено: {formatAmount(paymentsTotal)} ₸
                                    </Text>
                                </Col>
                                <Col span={8}>
                                    <Text type="secondary" style={{ fontSize: '12px', color: remainder > 0 ? '#ff4d4f' : '#52c41a' }}>
                                        Остаток: {formatAmount(Math.abs(remainder))} ₸
                                    </Text>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Row gutter={[8, 8]}>
                        {prepareData.available_payments.map(payment => (
                            <Col key={payment.payment_method_id} span={getColSpan()}>
                                <Card size="small">
                                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                        <Row justify="space-between" align="middle">
                                            <Text strong>{payment.payment_method_name}</Text>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                Макс. {formatAmount(payment.available_amount)} ₸
                                            </Text>
                                        </Row>
                                        <Input
                                            size="large"
                                            placeholder="0.00"
                                            value={selectedPayments[payment.payment_method_id] || ''}
                                            onChange={(e) => handlePaymentInputChange(payment.payment_method_id.toString(), e.target.value)}
                                            onFocus={() => setActivePaymentInput(payment.payment_method_id)}
                                            suffix="₸"
                                            style={{ fontSize: '18px', textAlign: 'center' }}
                                        />
                                    </Space>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                        <NumbersKeypad onKeyPress={handlePaymentKeyPress} />
                    </div>
                </Space>
            </div>
        );
    };

    // Рендер содержимого модала
    const renderContent = () => {
        if (loading) {
            return (
                <Space
                    direction="vertical"
                    size="large"
                    style={{ width: '100%', textAlign: 'center', padding: '40px 0' }}
                >
                    <Spin size="large" />
                </Space>
            );
        }

        if (!prepareData) {
            return (
                <Space
                    direction="vertical"
                    size="large"
                    style={{ width: '100%', textAlign: 'center', padding: '40px 0' }}
                >
                    <Text type="secondary">Нет данных для возврата</Text>
                </Space>
            );
        }

        return step === 1 ? renderItemsStep() : renderPaymentsStep();
    };

    return (
        <Modal
            title="Возврат товаров"
            open={visible}
            onCancel={handleModalClose}
            width={500}
            centered
            footer={
                step === 1 ? (
                    <Button
                        type="primary"
                        size="large"
                        onClick={handleNextStep}
                        style={{
                            height: '70px',
                            fontSize: '18px',
                            lineHeight: '23px',
                            letterSpacing: '-0.44px',
                            fontWeight: '600',
                            width: '100%'
                        }}
                    >
                        Продолжить
                    </Button>
                ) : (
                    <Row gutter={8}>
                        <Col span={4}>
                            <Button
                                size="large"
                                onClick={() => setStep(1)}
                                icon={<ArrowLeftOutlined />}
                                style={{
                                    height: '70px',
                                    fontSize: '18px',
                                    lineHeight: '23px',
                                    letterSpacing: '-0.44px',
                                    fontWeight: '600',
                                    width: '100%'
                                }}
                            />
                        </Col>
                        <Col span={20}>
                            <Button
                                type="primary"
                                size="large"
                                onClick={handleCreateReturn}
                                loading={actionLoading}
                                disabled={!isValidPayments()}
                                style={{
                                    height: '70px',
                                    fontSize: '18px',
                                    lineHeight: '23px',
                                    letterSpacing: '-0.44px',
                                    fontWeight: '600',
                                    width: '100%'
                                }}
                            >
                                Завершить
                            </Button>
                        </Col>
                    </Row>
                )
            }
        >
            {renderContent()}
            {/* Модал обработки платежа */}
            <Modal
                open={processingModal.visible}
                title={processingModal.title}
                closable={false}
                maskClosable={false}
                footer={null}
                centered
                width={400}
            >
                <div style={{
                    textAlign: 'center',
                    padding: '20px 0',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '20px'
                }}>
                    <Spin size="large" />
                    <Text style={{ fontSize: '16px' }}>
                        {processingModal.description}
                    </Text>
                </div>
            </Modal>
            <Modal
                open={errorModalVisible}
                title={null}
                closable={false}
                maskClosable={false}
                footer={null}
                centered
                width={450}
                onCancel={() => setErrorModalVisible(false)}
            >
                <div style={{ textAlign: 'center', fontSize: '20px', fontWeight: 600, lineHeight: '24px' }}>
                    Ошибка создания возврата
                </div>
                <div style={{
                    textAlign: 'center',
                    fontSize: '16px',
                    marginTop: '32px',
                    marginBottom: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}>
                    <div style={{ fontSize: '18px', marginBottom: '32px', marginTop: '8px' }}>
                        {errorMessage || 'Не удалось создать возврат. Попробуйте еще раз.'}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                        <Button
                            type="primary"
                            size="large"
                            style={{
                                height: '70px',
                                fontSize: '18px',
                                lineHeight: '23px',
                                letterSpacing: '-0.44px',
                                fontWeight: '600'
                            }}
                            onClick={() => setErrorModalVisible(false)}
                        >
                            Понятно
                        </Button>
                    </div>
                </div>
            </Modal>
        </Modal>
    );
};

export default SaleReturnModal;